import { createTheme, Input, type MantineColorsTuple } from '@mantine/core';
import inputClasses from './inputs.module.css';

// Фирменная палитра «Дата 1861» (тёмно-зелёная система с публичного лендинга).
// Источник: #294533 (тёмный), #b3e57a (лаймовый акцент), #dff2ca (светлый), #173020 (почти чёрно-зелёный).
// Точные тона можно довести через https://mantine.dev/colors-generator/.

// Primary — глубокий лесной зелёный (кнопки/ссылки/активные состояния, белый текст читается).
const brand: MantineColorsTuple = [
  '#edf7f0',
  '#d3e8da',
  '#aacfb8',
  '#7eb593',
  '#5a9d76',
  '#3f8560',
  '#2f6a4b',
  '#294533',
  '#1f3a29',
  '#173020',
];

// Пшенично-янтарный — единственный тёплый акцент палитры (поле и зерно: brand+lime+wheat).
// Применение ТОЧЕЧНОЕ: «★ Лучшее предложение», пустые состояния, предупреждающие счётчики.
const wheat: MantineColorsTuple = [
  '#fdf6ec',
  '#f7e9d0',
  '#f0d4a4',
  '#ecc077',
  '#e8ae52',
  '#e8a33d',
  '#d18a26',
  '#b1741a',
  '#8f5d13',
  '#6f470c',
];

// ── Семантические цвета «лучшей цены» (санкционированное исключение из триады brand+lime+wheat) ──
// Применяются ТОЛЬКО к элементу лучшей цены/дельты, не как цвета кабинета. Опорный тон — 6 (точные
// хексы согласованы продуктом). Заменили прежний лиловый best-price-язык: «лучшая цена» обеих сторон
// (Купить/Продать) — ОДИН оранжевый (ask); дельта — ВЫИГРЫШ/ПОТЕРЯ (gain/loss). См. design-guidelines §5.

// Оранжевый — единый цвет «лучшей цены» для ОБЕИХ сторон: «Продать по X» / «Купить по X», ориентир
// цены продажи. ЧИСТЫЙ оранжевый (не амбер/коричневый): читается ярко и «дорого». Опорный ask.6 #ea580c.
// Тон 7 (#c2410c) — заливка кнопки/крупная цена: белый текст на нём ≈5.2:1 (AA). Тон 0/2 — мягкая кнопка.
const ask: MantineColorsTuple = [
  '#fff7ed',
  '#ffedd5',
  '#fed7aa',
  '#fdba74',
  '#fb923c',
  '#f97316',
  '#ea580c',
  '#c2410c',
  '#9a3412',
  '#7c2d12',
];

// Зелёный — ВЫИГРЫШ в дельте относительно моей цены («Дешевле/Дороже» в мою пользу). Опорный gain.6 #16a34a.
const gain: MantineColorsTuple = [
  '#f0fdf4',
  '#dcfce7',
  '#bbf7d0',
  '#86efac',
  '#4ade80',
  '#22c55e',
  '#16a34a',
  '#15803d',
  '#166534',
  '#14532d',
];

// Красный — ПОТЕРЯ в дельте относительно моей цены (рынок против меня). Опорный loss.6 #dc2626.
const loss: MantineColorsTuple = [
  '#fef2f2',
  '#fee2e2',
  '#fecaca',
  '#fca5a5',
  '#f87171',
  '#ef4444',
  '#dc2626',
  '#b91c1c',
  '#991b1b',
  '#7f1d1d',
];

// Лаймовый акцент (бейджи/выделения/иллюстрации).
const lime: MantineColorsTuple = [
  '#f3fbe8',
  '#e4f6cd',
  '#d0eea8',
  '#bce97f',
  '#a6d95f',
  '#8fc742',
  '#74a532',
  '#5a8027',
  '#44611d',
  '#2e4212',
];

/** Основной шрифт интерфейса: Onest — текст, заголовки и цифры (решение 2026-10-29, заменил Golos, Inter, Manrope). */
const FONT_UI = "'Onest Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Машинные идентификаторы, которые сверяют по символам: номер сделки, ИНН, КПП, ОГРН, счёт, БИК, ключи и токены.
 * В коде — через компонент IdText и MONO_INPUT_STYLES. Цены, даты и объёмы им не набираются.
 */
const FONT_MONO = "'JetBrains Mono Variable', ui-monospace, SFMono-Regular, Menlo, monospace";

/** Поле ввода с машинным значением (API-ключ, адрес сервера): `<TextInput styles={MONO_INPUT_STYLES} />`. */
export const MONO_INPUT_STYLES = { input: { fontFamily: 'var(--mantine-font-family-monospace)' } } as const;

export const theme = createTheme({
  primaryColor: 'brand',
  // Анимации Mantine (меню, модальные окна) учитывают системную настройку «уменьшить движение».
  respectReducedMotion: true,
  primaryShade: { light: 7, dark: 6 },
  defaultRadius: 'md',
  // Курсор-указатель на чекбоксах/радио/switch и native select — чтобы было видно, что кликабельно.
  cursorType: 'pointer',
  colors: { brand, lime, wheat, ask, gain, loss },
  // Onest — одна гарнитура на весь интерфейс; заголовки отличаются жирностью, а не шрифтом.
  fontFamily: FONT_UI,
  fontFamilyMonospace: FONT_MONO,
  headings: {
    fontFamily: FONT_UI,
  },
  other: {
    headerHeight: 64,
  },
  components: {
    // Светло-зелёный — основной стиль кнопок: фон lime.4 + тёмно-зелёный текст brand.9
    // (как у CTA «Зарегистрироваться» в hero). Вторичные кнопки задают свой variant/c явно.
    Button: {
      defaultProps: {
        color: 'lime.4',
        c: 'brand.9',
        // Базовая высота кнопок — md (42px), как в hero. Исключение — кнопки в шапке: им явно
        // задан size="sm" (36px). Компактные действия используют compact-* при необходимости.
        size: 'md',
      },
    },
    // Фирменный стиль полей (см. inputs.module.css). Каскадит на все Input-based компоненты
    // (TextInput/PasswordInput/Select/NativeSelect/...): высота 48, шрифт 16, рамка #cbd5e1, радиус 4.
    Input: Input.extend({
      defaultProps: { size: 'md' },
      classNames: { input: inputClasses.input },
    }),
    // Подсказки и ошибки полей объясняют форму, поэтому читаемые: 14 px и контраст не ниже AA (по умолчанию 12 px,
    // бледный серый и красный 3,28:1). Действует на все формы портала.
    InputWrapper: Input.Wrapper.extend({
      styles: {
        description: { fontSize: 'var(--mantine-font-size-sm)', color: 'var(--mantine-color-gray-7)' },
        error: { fontSize: 'var(--mantine-font-size-sm)', color: 'var(--mantine-color-loss-7)' },
      },
    }),
    // Чекбоксы/радио — зафиксированный размер sm (бокс 20×20, лейбл 14). См. design-guidelines «Размеры».
    Checkbox: { defaultProps: { size: 'sm' } },
    Radio: { defaultProps: { size: 'sm' } },
  },
});
